/* eslint-disable no-unused-vars */
import { AlertTriangle, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import React from 'react';
import type { RuntimeConfigValueDraft } from '../../types/dialogs';
import {
  validateRuntimeConfigValue,
  validateRuntimeConfigValueKey
} from '../../utils/runtimeConfigValueValidation';
import { FieldTooltip } from '../FieldTooltip';

type RuntimeConfigValueListEditorProps = {
  values: RuntimeConfigValueDraft[];
  onChange: (nextValues: RuntimeConfigValueDraft[]) => void;
};

const MASKED_PLACEHOLDER = '••••••••••••';

function emptyDraft(): RuntimeConfigValueDraft {
  return {
    draftId: crypto.randomUUID(),
    key: '',
    value: '',
    valueType: 'string',
    masked: false,
    valueTouched: true
  };
}

function RuntimeConfigValueListEditor({
  values,
  onChange
}: RuntimeConfigValueListEditorProps) {
  const [revealed, setRevealed] = React.useState<Set<string>>(new Set());

  function rowIdentity(row: RuntimeConfigValueDraft, index: number) {
    return row.runtimeConfigValueId || row.draftId || `${row.key}:${index}`;
  }

  function updateRow(index: number, patch: Partial<RuntimeConfigValueDraft>) {
    onChange(
      values.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  }

  function toggleRevealed(identity: string) {
    setRevealed((previous) => {
      const next = new Set(previous);
      if (next.has(identity)) next.delete(identity);
      else next.add(identity);
      return next;
    });
  }

  const keyCounts = new Map<string, number>();
  for (const row of values) {
    const key = row.key.trim();
    if (key) keyCounts.set(key, (keyCounts.get(key) || 0) + 1);
  }

  return (
    <div className="settingsGroup runtimeValuesGroup">
      <div className="runtimeValuesHeading">
        <div>
          <div className="settingsGroupLabel">
            Values sent to the app
            <FieldTooltip text="These values are sent with the settings above when an app matches this rule." />
          </div>
          <p className="fieldHint">Add values your app reads at runtime.</p>
        </div>
        <span className="runtimeValueCount">
          {values.length} {values.length === 1 ? 'value' : 'values'}
        </span>
      </div>

      <div className="runtimeValuesNotice">
        <AlertTriangle size={15} />
        <span>
          The app can read every value in this rule. &apos;Hide in admin&apos;
          only masks it here. Keep server credentials out.
        </span>
      </div>

      {values.length > 0 ? (
        <div className="runtimeValueColumns" aria-hidden="true">
          <span>Key</span>
          <span>Value</span>
          <span>Type</span>
          <span>Visibility</span>
          <span />
        </div>
      ) : (
        <div className="runtimeValuesEmpty">
          <strong>No extra values</strong>
          <span>This rule will send the endpoints and settings above.</span>
        </div>
      )}

      <div className="runtimeValueRows">
        {values.map((row, index) => {
          const identity = rowIdentity(row, index);
          const key = row.key.trim();
          const keyError = key
            ? validateRuntimeConfigValueKey(key) ||
              ((keyCounts.get(key) || 0) > 1
                ? 'Duplicate key in this rule.'
                : null)
            : null;
          const showValueInput =
            !row.masked || !row.runtimeConfigValueId || row.valueTouched;
          const valueError =
            showValueInput && row.valueTouched
              ? validateRuntimeConfigValue(row.value, row.valueType)
              : null;
          const isRevealed = revealed.has(identity);

          return (
            <div key={identity} className="runtimeValueRow">
              <label className="settingsField runtimeValueKey">
                <span className="runtimeValueMobileLabel">Key</span>
                <input
                  value={row.key}
                  onChange={(event) =>
                    updateRow(index, { key: event.target.value })
                  }
                  placeholder="featureFlag"
                />
                {keyError ? <p className="fieldError">{keyError}</p> : null}
              </label>

              <label className="settingsField runtimeValueInput">
                <span className="runtimeValueMobileLabel">Value</span>
                {showValueInput ? (
                  row.valueType === 'boolean' ? (
                    <select
                      value={row.value}
                      onChange={(event) =>
                        updateRow(index, {
                          value: event.target.value,
                          valueTouched: true
                        })
                      }
                    >
                      <option value="">Choose…</option>
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  ) : row.valueType === 'json' ? (
                    <textarea
                      rows={2}
                      value={row.value}
                      onChange={(event) =>
                        updateRow(index, {
                          value: event.target.value,
                          valueTouched: true
                        })
                      }
                      placeholder='{"key":"value"}'
                    />
                  ) : (
                    <input
                      type={
                        row.masked && !isRevealed
                          ? 'password'
                          : row.valueType === 'number'
                            ? 'number'
                            : 'text'
                      }
                      value={row.value}
                      onChange={(event) =>
                        updateRow(index, {
                          value: event.target.value,
                          valueTouched: true
                        })
                      }
                      placeholder={row.valueType === 'number' ? '0' : 'value'}
                    />
                  )
                ) : (
                  <div className="runtimeMaskedValue">
                    <input value={MASKED_PLACEHOLDER} disabled />
                    <button
                      type="button"
                      className="secondaryButton"
                      onClick={() =>
                        updateRow(index, { valueTouched: true, value: '' })
                      }
                    >
                      Change
                    </button>
                  </div>
                )}
                {valueError ? <p className="fieldError">{valueError}</p> : null}
              </label>

              <label className="settingsField runtimeValueType">
                <span className="runtimeValueMobileLabel">Type</span>
                <select
                  value={row.valueType}
                  onChange={(event) => {
                    const valueType = event.target
                      .value as RuntimeConfigValueDraft['valueType'];
                    updateRow(index, {
                      valueType,
                      value:
                        valueType === 'boolean' &&
                        !['true', 'false'].includes(row.value)
                          ? ''
                          : row.value
                    });
                  }}
                >
                  <option value="string">Text</option>
                  <option value="boolean">True / false</option>
                  <option value="number">Number</option>
                  <option value="json">JSON</option>
                </select>
              </label>

              <div className="runtimeValueVisibility">
                <label title="'Hide in admin' masks this value on this screen. Apps can still read it.">
                  <input
                    type="checkbox"
                    checked={row.masked}
                    onChange={(event) =>
                      updateRow(index, { masked: event.target.checked })
                    }
                  />
                  Hide in admin
                </label>
                {row.masked && showValueInput ? (
                  <button
                    type="button"
                    className="iconButton"
                    onClick={() => toggleRevealed(identity)}
                    aria-label={isRevealed ? 'Hide value' : 'Reveal value'}
                  >
                    {isRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                ) : null}
              </div>

              <button
                type="button"
                className="iconButton runtimeValueRemove"
                onClick={() => {
                  setRevealed((previous) => {
                    if (!previous.has(identity)) return previous;
                    const next = new Set(previous);
                    next.delete(identity);
                    return next;
                  });
                  onChange(values.filter((_, rowIndex) => rowIndex !== index));
                }}
                aria-label={`Remove ${row.key || 'value'}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="secondaryButton runtimeAddValueButton"
        onClick={() => onChange([...values, emptyDraft()])}
      >
        <Plus size={14} />
        Add value
      </button>
    </div>
  );
}

export { RuntimeConfigValueListEditor };
